"use client";

import clsx from "clsx";
import * as Icon from "lucide-react";
import { useState, useRef, useEffect } from "react";
import type { PageHref } from "@/ui/util";
import type { MouseEvent } from "react";
import { Link, ExternalLink } from "@/ui/base/Link";
import ReactMarkdown from "react-markdown";


type PressHandler = (event: MouseEvent<HTMLButtonElement>) => any | Promise<any>;


export function Button({ ...props }: ButtonProps & {
  label?: string;
  tooltip?: string;
  className?: string;

  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;

  sm?: boolean;
  md?: boolean; // DEFAULT: small on mobile, large on desktop
  lg?: boolean; // big on both desktop and mobile
  mobileMd?: boolean;
  mobileLg?: boolean;
  desktopSm?: boolean;
  desktopMd?: boolean;
  desktopLg?: boolean;
}) {
  const button = useButton(props);

  const styleDesktopSm = "xl:h-[37px] xl:text-[14px] xl:px-[18px] py-[10px]";
  const styleDesktopMd = "xl:h-[43px] xl:text-[16px] xl:px-[24px] py-[12px]";
  const styleDesktopLg = "xl:h-[46px] xl:text-[18px] xl:px-[24px] py-[12px]";

  const styleMobileMd = "h-[37px] text-[14px] px-[18px] py-[10px]";
  const styleMobileLg = "h-[43px] text-[16px] px-[24px] py-[12px]";

  const styleMobile = (
    props.mobileLg ? styleMobileLg :
      props.mobileMd ? styleMobileMd :
        props.lg ? styleMobileLg :
          styleMobileMd
  );

  const styleDesktop = (
    props.desktopLg ? styleDesktopLg :
      props.desktopMd ? styleDesktopMd :
        props.desktopSm ? styleDesktopSm :
          props.lg ? styleDesktopLg :
            props.sm ? styleDesktopSm :
              styleDesktopMd
  );

  const className = clsx(
    props.className,
    "relative min-w-control px-md rounded",
    "inline-flex flex-row flex-nowrap items-center justify-center gap-sm",
    "transition-transform transition-98",
    "rounded-[8px]",
    "font-medium",
    styleMobile,
    styleDesktop,

    button.pressed && "scale-92",
    button.isDisabled() && "cursor-default",
    button.isIdle() && "cursor-pointer",

    (
      button.isDisabled() ? "text-black/30 bg-black/3" : (
        props.primary ? "bg-yellow text-black hover:bg-black hover:text-white" :
          props.secondary ? "bg-black text-white hover:bg-surface-gray-darker hover:text-black" :
            props.tertiary ? "border border-black text-black hover:bg-black hover:text-white" :
              "hover:bg-black/3"
      )
    ),
  );

  const children = (
    <>
      <div className="overflow-hidden flex flex-col gap-xs">
        {props.label && <div className={`truncate text-center ${button.isProcessing() ? "text-transparent" : ""}`}>
          <ReactMarkdown>
            {props.label}
          </ReactMarkdown>

        </div>}
      </div>
      {button.isProcessing() && <ButtonSpinner />}
    </>
  );

  if (props.exitHref) {
    return (
      <ExternalLink
        ref={props.ref as React.RefObject<HTMLAnchorElement> | undefined}
        href={props.exitHref}
        title={props.tooltip}
        onPointerDown={button.setPressed}
        onPointerOut={button.setReleased}
        onPointerUp={button.setReleased}
        onClick={props.onClick}
        className={className}
      >
        {children}
      </ExternalLink>
    );
  }

  if (props.href) {
    return (
      <Link
        ref={props.ref as React.RefObject<HTMLAnchorElement> | undefined}
        href={props.href}
        title={props.tooltip}
        onPointerDown={button.setPressed}
        onPointerOut={button.setReleased}
        onPointerUp={button.setReleased}
        onClick={props.onClick}
        className={className}
      >
        {children}
      </Link>
    );
  }

  return (
    <button
      ref={props.ref as React.RefObject<HTMLButtonElement> | undefined}
      type={button.type}
      form={button.form}
      title={props.tooltip}

      onClick={button.handlePress}
      onPointerDown={button.setPressed}
      onPointerOut={button.setReleased}
      onPointerUp={button.setReleased}

      disabled={button.isDisabled()}
      className={className}
    >
      {children}
    </button>
  );
}


function ButtonSpinner() {
  return (
    <span
      children={<Icon.LoaderCircle />}
      className={`
        absolute inset-0
        flex items-center justify-center
        bg-inherit rounded
      `}
    />
  );
}


type ButtonProps = (
  | PushButtonProps
  | InternalLinkButtonProps
  | ExternalLinkButtonProps
);

type PushButtonProps = {
  href?: never;
  onClick?: PressHandler;
  exitHref?: never;

  ref?: React.RefObject<HTMLButtonElement>;
  submit?: boolean | string;
  processing?: boolean;
  disabled?: boolean;
};

type InternalLinkButtonProps = {
  href: PageHref;
  onClick?: React.MouseEventHandler;
  exitHref?: never;

  ref?: React.RefObject<HTMLAnchorElement>;
  submit?: undefined;
  processing?: undefined;
  disabled?: undefined;
};

type ExternalLinkButtonProps = {
  href?: never;
  onClick?: React.MouseEventHandler;
  exitHref: string;

  ref?: React.RefObject<HTMLAnchorElement>;
  submit?: undefined;
  processing?: undefined;
  disabled?: undefined;
};


function useButton(props: ButtonProps) {
  // used to identify the last action to be able to stp the internal processing state
  const lastActionPromise = useRef<PromiseLike<any> | undefined>(undefined);

  type State = "disabled" | "processing" | "idle";

  const [, setRenderState] = useState<0 | 1>(1);
  const forceRender = () => setRenderState(prev => (1 - prev) as 0 | 1);
  const stateRef = useRef<State>(
    props.disabled ? "disabled" :
      props.processing ? "processing" :
        "idle"
  );

  // absolutely hackish but quick fix to port the button without the whole lib
  useEffect(() => {
    stateRef.current = (
      props.disabled ? "disabled" :
        props.processing ? "processing" :
          "idle"
    );
    forceRender();
  }, [props.disabled, props.processing]);

  // was pressed (used to animate the button)
  const [pressed, setPressedInternal] = useState<boolean>(false);

  const handlePress = (event: MouseEvent<HTMLButtonElement>) => {
    if (stateRef.current !== "idle") {
      return;
    }

    if (props.ref?.current) {
      setPressedInternal(false);
      props.ref.current.classList.remove("--pressed");
      props.ref.current.blur();
    }

    if (props.onClick) {
      lastActionPromise.current = undefined;

      props.onClick(event);
    }
  };

  const setPressed = () => {
    if (stateRef.current !== "idle") {
      return;
    }

    setPressedInternal(true);
    props.ref?.current?.classList.add("--pressed");
  };

  const setReleased = () => {
    setPressedInternal(false);
    props.ref?.current?.classList.remove("--pressed");
  };


  const type = typeof props.submit === "string" || !!props.submit ? "submit" : "button";

  const form = typeof props.submit === "string" ? props.submit : undefined;

  return {
    handlePress,
    pressed,
    stateRef: stateRef as { readonly current: State },
    setPressed,
    setReleased,
    isIdle: () => stateRef.current === "idle",
    isDisabled: () => stateRef.current === "disabled",
    isProcessing: () => stateRef.current === "processing",
    type,
    form,
  } as const;
}
